import { Box, Group, Paper, Stack, Text } from '@mantine/core';
import { forwardRef } from 'react';
import { usePreferencesStore } from '../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../hooks/useAccessibilityStyles';
import {
  colorSchemes,
  getColorScheme,
  getPrimaryGradient,
  getSecondaryGradient,
  getTertiaryGradient,
  type ColorScheme,
} from '../../theme/theme';

export interface ColorSchemePreviewProps {
  schemeKey?: string;
  showLabel?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: (scheme: ColorScheme) => void;
  selected?: boolean;
}

const sizeMap = {
  xs: { bar: 20, gap: 4 },
  sm: { bar: 32, gap: 6 },
  md: { bar: 48, gap: 8 },
  lg: { bar: 64, gap: 10 },
};

const ColorSchemePreview = forwardRef<HTMLDivElement, ColorSchemePreviewProps>(
  ({ schemeKey, showLabel = true, size = 'md', onClick, selected = false }, ref) => {
    const scheme = schemeKey ? getColorScheme(schemeKey) : colorSchemes[0];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    if (!scheme) return null;

    const { bar, gap } = sizeMap[size];

    return (
      <Paper
        ref={ref}
        p={size === 'xs' ? 'xs' : 'sm'}
        radius="md"
        withBorder
        style={{
          cursor: onClick ? 'pointer' : 'default',
          borderColor: selected ? 'var(--mantine-primary-color-filled)' : undefined,
          borderWidth: selected ? 2 : 1,
          transition: 'all 0.2s ease',
        }}
        onClick={() => onClick?.(scheme)}
      >
        <Stack gap={gap}>
          <Group gap={gap}>
            <Box
              style={{
                flex: 1,
                height: bar,
                borderRadius: 6,
                background: getPrimaryGradient(scheme),
              }}
            />
            <Box
              style={{
                flex: 1,
                height: bar,
                borderRadius: 6,
                background: getSecondaryGradient(scheme),
              }}
            />
            <Box
              style={{
                flex: 1,
                height: bar,
                borderRadius: 6,
                background: getTertiaryGradient(scheme),
              }}
            />
          </Group>
          {showLabel && (
            <Text size={size === 'xs' ? 'xs' : 'sm'} ta="center" fw={500} style={accessibilityStyles}>
              {scheme.emoji} {scheme.label}
            </Text>
          )}
        </Stack>
      </Paper>
    );
  }
);

ColorSchemePreview.displayName = 'ColorSchemePreview';

export default ColorSchemePreview;
